import { Button, Col, Dropdown, Menu, Modal, Row, TimePicker, message } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CopyOutlined, SettingOutlined } from "@ant-design/icons";
import lang from "../../../services/lang";
import "./ShiftSettings.css";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const WEEKENDS = ["saturday", "sunday"];

function ShiftSettings(props) {
  const { langIndex } = useSelector((x) => x.app);
  const { showModal, setShowModal, customerInfo, updateCustomerInfo } = props;

  const [selectedDay, setSelectedDay] = useState(moment().format("dddd").toLowerCase());
  const [shifts, setShifts] = useState({});
  const [shift1_start, setShift1_start] = useState();
  const [shift1_end, setShift1_end] = useState();
  const [shift2_start, setShift2_start] = useState();
  const [shift2_end, setShift2_end] = useState();
  const [shift3_start, setShift3_start] = useState();
  const [shift3_end, setShift3_end] = useState();

  const format = "HH:mm";

  // Initialize shifts data structure
  useEffect(() => {
    let shiftsData = {};
    
    // Check if shifts_setting exists (new format)
    if (customerInfo.shifts_setting && typeof customerInfo.shifts_setting === 'string') {
      try {
        shiftsData = JSON.parse(customerInfo.shifts_setting);
      } catch (e) {
        shiftsData = {};
      }
    } else if (customerInfo.shifts_setting && typeof customerInfo.shifts_setting === 'object') {
      shiftsData = customerInfo.shifts_setting;
    }
    
    // If no shifts_setting, migrate from old format
    if (!shiftsData || Object.keys(shiftsData).length === 0) {
      
      const defaultShift = {
        shift1_start: customerInfo.shift1_start || "",
        shift1_end: customerInfo.shift1_end || "",
        shift2_start: customerInfo.shift2_start || "",
        shift2_end: customerInfo.shift2_end || "",
        shift3_start: customerInfo.shift3_start || "",
        shift3_end: customerInfo.shift3_end || "",
      };
      
      // Apply to all days
      DAYS.forEach(day => {
        shiftsData[day] = { ...defaultShift };
      });
    }
    
    // Ensure all days exist
    DAYS.forEach(day => {
      if (!shiftsData[day]) {
        shiftsData[day] = {
          shift1_start: "",
          shift1_end: "",
          shift2_start: "",
          shift2_end: "",
          shift3_start: "",
          shift3_end: "",
        };
      }
    });
    
    setShifts(shiftsData);
    loadDayShifts(shiftsData, selectedDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo]);

  // Load shifts for selected day
  const loadDayShifts = (shiftsData, day) => {
    const dayShifts = shiftsData[day] || {};
    
    setShift1_start(dayShifts.shift1_start && dayShifts.shift1_start !== "" && dayShifts.shift1_end !== "00:00" 
      ? moment(dayShifts.shift1_start, format) : null);
    setShift1_end(dayShifts.shift1_end && dayShifts.shift1_end !== "00:00" 
      ? moment(dayShifts.shift1_end, format) : null);
    setShift2_start(dayShifts.shift2_start && dayShifts.shift2_start !== "" && dayShifts.shift2_end !== "00:00" 
      ? moment(dayShifts.shift2_start, format) : null);
    setShift2_end(dayShifts.shift2_end && dayShifts.shift2_end !== "00:00" 
      ? moment(dayShifts.shift2_end, format) : null);
    setShift3_start(dayShifts.shift3_start && dayShifts.shift3_start !== "" && dayShifts.shift3_end !== "00:00" 
      ? moment(dayShifts.shift3_start, format) : null);
    setShift3_end(dayShifts.shift3_end && dayShifts.shift3_end !== "00:00" 
      ? moment(dayShifts.shift3_end, format) : null);
  };

  // Handle day tab change
  const onDayChange = (day) => {
    // Save current day's shifts before switching
    const updatedShifts = { ...shifts };
    updatedShifts[selectedDay] = {
      shift1_start: shift1_start != null ? shift1_start.format(format) : "",
      shift1_end: shift1_end != null ? shift1_end.format(format) : "",
      shift2_start: shift2_start != null ? shift2_start.format(format) : "",
      shift2_end: shift2_end != null ? shift2_end.format(format) : "",
      shift3_start: shift3_start != null ? shift3_start.format(format) : "",
      shift3_end: shift3_end != null ? shift3_end.format(format) : "",
    };
    setShifts(updatedShifts);
    setSelectedDay(day);
    loadDayShifts(updatedShifts, day);
  };


  const onShift1StartChange = (time) => {
    setShift1_start(time);

    setShift1_end(null);
    setShift2_start(null);
    setShift2_end(null);
    setShift3_start(null);
    setShift3_end(null);
  };

  const onShift1EndChange = (time) => {
    setShift1_end(time);
    if (time < shift1_start || shift1_start === null) {
      setShift1_end(null);
    }

    setShift2_start(null);
    setShift2_end(null);
    setShift3_start(null);
    setShift3_end(null);
  };

  const onShift2StartChange = (time) => {
    setShift2_start(time);
    if (time < shift1_end || shift1_end === null) {
      setShift2_start(null);
    }

    setShift2_end(null);
    setShift3_start(null);
    setShift3_end(null);
  };

  const onShift2EndChange = (time) => {
    setShift2_end(time);
    if ((time < shift2_start && time > shift1_start) || shift2_start === null) {
      setShift2_end(null);
    }

    setShift3_start(null);
    setShift3_end(null);
  };

  const onShift3StartChange = (time) => {
    setShift3_start(time);
    if (time < shift2_end || shift2_end === null) {
      setShift3_start(null);
    }
    setShift3_end(null);
  };

  const onShift3EndChange = (time) => {
    setShift3_end(time);
    if ((time < shift3_start && time > shift1_start) || shift3_start === null) {
      setShift3_end(null);
    }
  };

  // Copy shifts to other days
  const copyToDays = (targetDays) => {
    // First save current day's shifts
    const currentDayShifts = {
      shift1_start: shift1_start != null ? shift1_start.format(format) : "",
      shift1_end: shift1_end != null ? shift1_end.format(format) : "",
      shift2_start: shift2_start != null ? shift2_start.format(format) : "",
      shift2_end: shift2_end != null ? shift2_end.format(format) : "",
      shift3_start: shift3_start != null ? shift3_start.format(format) : "",
      shift3_end: shift3_end != null ? shift3_end.format(format) : "",
    };
    
    const updatedShifts = { ...shifts };
    // Update current day first
    updatedShifts[selectedDay] = { ...currentDayShifts };
    // Copy to target days
    targetDays.forEach(day => {
      updatedShifts[day] = { ...currentDayShifts };
    });
    setShifts(updatedShifts);
    message.success(lang(langIndex, "msg_update_success") || "Settings copied successfully");
  };

  const onUpdateShift = () => {
    if (shift1_start == null && shift2_start == null && shift3_start == null) {
      message.error("Invalid shift time");
      return;
    }

    if (shift1_start != null && shift1_end == null) {
      message.error("Invalid shift time");
      return;
    }
    if (shift2_start != null && shift2_end == null) {
      message.error("Invalid shift time");
      return;
    }
    if (shift3_start != null && shift3_end == null) {
      message.error("Invalid shift time");
      return;
    }

    // Save current day's shifts to state first
    const currentDayShifts = {
      shift1_start: shift1_start != null ? shift1_start.format(format) : "",
      shift1_end: shift1_end != null ? shift1_end.format(format) : "",
      shift2_start: shift2_start != null ? shift2_start.format(format) : "",
      shift2_end: shift2_end != null ? shift2_end.format(format) : "",
      shift3_start: shift3_start != null ? shift3_start.format(format) : "",
      shift3_end: shift3_end != null ? shift3_end.format(format) : "",
    };
    
    const updatedShifts = { ...shifts };
    updatedShifts[selectedDay] = { ...currentDayShifts };
    
    // Update customerInfo with shifts_setting and current day's shifts
    let newInfo = { ...customerInfo };
    newInfo.shifts_setting = JSON.stringify(updatedShifts);
    
    // Update original fields with current day's settings (for backward compatibility)
    newInfo.shift1_start = currentDayShifts.shift1_start;
    newInfo.shift1_end = currentDayShifts.shift1_end;
    newInfo.shift2_start = currentDayShifts.shift2_start;
    newInfo.shift2_end = currentDayShifts.shift2_end;
    newInfo.shift3_start = currentDayShifts.shift3_start;
    newInfo.shift3_end = currentDayShifts.shift3_end;
    
    updateCustomerInfo(newInfo);
  };

  const onCancel = () => {
    setShowModal(false);
  };

  const getDayLabel = (day) => {
    const dayLabels = {
      monday: "Mon",
      tuesday: "Tue",
      wednesday: "Wed",
      thursday: "Thu",
      friday: "Fri",
      saturday: "Sat",
      sunday: "Sun",
    };
    return dayLabels[day] || day;
  };

  const copyMenu = (
    <Menu>
      <Menu.Item key="all" onClick={() => copyToDays(DAYS)}>
        Copy to All Days
      </Menu.Item>
      <Menu.Item key="weekdays" onClick={() => copyToDays(WEEKDAYS)}>
        Copy to Weekdays
      </Menu.Item>
      <Menu.Item key="weekends" onClick={() => copyToDays(WEEKENDS)}>
        Copy to Weekends
      </Menu.Item>
    </Menu>
  );

  return (
    <div>
      <Modal
        centered
        visible={showModal}
        title={null}
        onCancel={() => onCancel()}
        onOk={() => onUpdateShift()}
        okText={lang(langIndex, "plant_update")}
        closable={true}
        className="customer-setting-dialog-style"
        destroyOnClose={true}
        width={700}
      >
        <div className="customer-setting-page-title">
          <SettingOutlined />
          <span style={{ marginLeft: 10 }} />
          {lang(langIndex, "shift_settings")}
        </div>
        
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <div className="shift-day-selector-container">
              <div className="shift-day-group">
                <span className="shift-day-group-label">Weekdays</span>
                <div className="shift-day-buttons">
                  {WEEKDAYS.map(day => (
                    <button
                      key={day}
                      className={`shift-day-button ${selectedDay === day ? 'shift-day-button-active' : ''}`}
                      onClick={() => onDayChange(day)}
                    >
                      {getDayLabel(day)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="shift-day-group" style={{ marginTop: 12 }}>
                <span className="shift-day-group-label">Weekends</span>
                <div className="shift-day-buttons">
                  {WEEKENDS.map(day => (
                    <button
                      key={day}
                      className={`shift-day-button ${selectedDay === day ? 'shift-day-button-active' : ''}`}
                      onClick={() => onDayChange(day)}
                    >
                      {getDayLabel(day)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Dropdown overlay={copyMenu} trigger={['click']}>
              <Button type="text" icon={<CopyOutlined />} className="shift-copy-button">
                Copy
              </Button>
            </Dropdown>
          </div>
        </div>

        <div className="customer-setting-container-style">
          <div className="app-setting-text-input-title">
            {" "}
            {lang(langIndex, "shift")} 1
          </div>
          <Row style={{ marginTop: 5, marginBottom: 15 }}>
            <Col span={11}>
              <TimePicker
                className="shift-setting-time-picker-style"
                value={shift1_start}
                style={{}}
                onChange={onShift1StartChange}
                format={format}
                placeholder="Start Time"
              />
            </Col>
            <Col
              className="shift-title-style"
              span={1}
              style={{ textAlign: "center" }}
            >
              ~
            </Col>
            <Col span={11}>
              <TimePicker
                className="shift-setting-time-picker-style"
                value={shift1_end}
                onChange={onShift1EndChange}
                format={format}
                placeholder="End Time"
              />
            </Col>
          </Row>

          <div className="app-setting-text-input-title">
            {" "}
            {lang(langIndex, "shift")} 2
          </div>
          <Row style={{ marginTop: 5, marginBottom: 15 }}>
            <Col span={11}>
              <TimePicker
                className="shift-setting-time-picker-style"
                value={shift2_start}
                onChange={onShift2StartChange}
                format={format}
                placeholder="Start Time"
              />
            </Col>
            <Col
              className="shift-title-style"
              span={1}
              style={{ textAlign: "center" }}
            >
              ~
            </Col>
            <Col span={11}>
              <TimePicker
                className="shift-setting-time-picker-style"
                value={shift2_end}
                onChange={onShift2EndChange}
                format={format}
                placeholder="End Time"
              />
            </Col>
          </Row>

          <div className="app-setting-text-input-title">
            {" "}
            {lang(langIndex, "shift")} 3
          </div>
          <Row style={{ marginTop: 5, marginBottom: 15 }}>
            <Col span={11}>
              <TimePicker
                className="shift-setting-time-picker-style"
                value={shift3_start}
                onChange={onShift3StartChange}
                format={format}
                placeholder="Start Time"
              />
            </Col>
            <Col
              className="shift-title-style"
              span={1}
              style={{ textAlign: "center" }}
            >
              ~
            </Col>
            <Col span={11}>
              <TimePicker
                className="shift-setting-time-picker-style"
                value={shift3_end}
                onChange={onShift3EndChange}
                format={format}
                placeholder="End Time"
              />
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
}

export default ShiftSettings;
